import React, { useMemo, useState, useEffect } from 'react';
import { useEntityRouter } from '../hooks/useEntityRouter';
import { Box, Button, Stack, TextField, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listPartners,
  createPartner,
  updatePartner,
  deletePartner,
  Partner,
} from '../services/partnerService';
import { listBusinessTypes, BusinessType } from '../services/businessTypeService';
import DataTable from '../components/DataTable';
import FormDialog from '../components/FormDialog';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import BusinessTypeMultiSelect from '../components/BusinessTypeMultiSelect';

const schema = yup.object({
  name: yup.string().required(),
  business_type_ids: yup.array().of(yup.number()).required(),
});

type FormValues = yup.InferType<typeof schema>;

const PartnersPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { entityId, navigateToEntity, navigateToList } = useEntityRouter('/partners');
  const [openForm, setOpenForm] = useState(false);
  const [editData, setEditData] = useState<Partner | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);

  useEffect(() => {
    listBusinessTypes().then(setBusinessTypes);
  }, []);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: { name: '', business_type_ids: [] },
  });

  const { data: partners = [], isLoading } = useQuery({ queryKey: ['partners'], queryFn: listPartners });

  // Load specific partner if entityId is present
  useEffect(() => {
    if (entityId && partners.length > 0) {
      const partner = partners.find((p: Partner) => p.id === parseInt(entityId));
      if (partner) {
        handleEdit(partner);
      }
    }
  }, [entityId, partners]);

  const createMut = useMutation({
    mutationFn: createPartner,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['partners'] }),
  });
  const updateMut = useMutation({
    mutationFn: (payload: Partner) => updatePartner(payload.id!, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['partners'] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deletePartner(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['partners'] }),
  });

  const onSubmit = (vals: FormValues) => {
    const cleanVals = {
      ...vals,
      business_type_ids: (vals.business_type_ids ?? []).filter((v): v is number => typeof v === 'number'),
    };
    if (editData) updateMut.mutate({ ...editData, ...cleanVals });
    else createMut.mutate(cleanVals);
    setOpenForm(false);
    // Navigate back to list view after saving
    if (entityId) {
      navigateToList();
    }
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    // Navigate back to list view when closing without saving
    if (entityId) {
      navigateToList();
    }
  };

  const handleAdd = () => {
    reset({ name: '', business_type_ids: [] });
    setEditData(null);
    setOpenForm(true);
  };

  const handleEdit = (row: Partner) => {
    setEditData(row);
    const ids = Array.isArray(row.business_type_ids)
      ? row.business_type_ids.filter((v): v is number => typeof v === 'number')
      : [];
    reset({
      name: row.name,
      business_type_ids: ids,
    });
    setOpenForm(true);
  };

  const handleRowClick = (row: Partner) => {
    navigateToEntity(row.id!);
  };

  const columns = useMemo(
    () => [
      { field: 'name', headerName: t('partners.name'), flex: 1 },
      {
        field: 'business_type_ids',
        headerName: t('navigation.businessTypes'),
        flex: 1,
        valueGetter: (params: any) => {
          if (!params) return '-';
          if (Array.isArray(params) && params.length > 0) {
            return params.map((id: number) => businessTypes.find((bt) => bt.id === id)?.name || id)
              .join(', ');
          }
          return '-';
        }
      },
      {
        field: 'actions',
        headerName: t('partners.actions'),
        width: 120,
        sortable: false,
        renderCell: (params: any) => (
          <Stack direction="row" spacing={1}>
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => setDeleteId(params.row.id)}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [businessTypes]
  );

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <h2>{t('partners.title')}</h2>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          {t('partners.add')}
        </Button>
      </Stack>

      <DataTable 
        rows={partners} 
        columns={columns} 
        loading={isLoading} 
        onRowDoubleClick={handleEdit}
        onRowClick={handleRowClick}
      />

      <FormDialog open={openForm} title={editData ? t('partners.edit') : t('partners.add')} onClose={handleCloseForm} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2} pt={1}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField label="Name" {...field} fullWidth error={!!errors.name} helperText={errors.name?.message} />
            )}
          />
          <Controller
            name="business_type_ids"
            control={control}
            render={({ field }) => (
              <BusinessTypeMultiSelect
                value={(field.value ?? []).filter((v): v is number => typeof v === 'number')}
                onChange={field.onChange}
                options={businessTypes}
                error={!!errors.business_type_ids}
                helperText={errors.business_type_ids?.message}
              />
            )}
          />
        </Stack>
      </FormDialog>

      <ConfirmDeleteDialog
        open={deleteId !== null}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => {
          if (deleteId) deleteMut.mutate(deleteId);
          setDeleteId(null);
        }}
      />
    </Box>
  );
};

export default PartnersPage; 